import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';

const SECURITY_EVENT_TYPES = [
  'all',
  'LOGIN',
  'LOGIN_FAILED',
  'LOGOUT',
  'ROLE_CHANGE',
  'PERMISSION_CHANGE',
  'PASSWORD_RESET',
  'ACCOUNT_LOCKED',
  'UNAUTHORIZED_ACCESS',
  'DELETE',
  'EXPORT',
];

const SEVERITY_CONFIG = {
  info: { label: 'Info', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  warning: { label: 'Warning', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  error: { label: 'Error', color: 'bg-red-100 text-red-700 border-red-200' },
  critical: { label: 'Critical', color: 'bg-purple-100 text-purple-700 border-purple-200' },
};

const STATUS_CONFIG = {
  success: { label: 'Success', color: 'bg-green-100 text-green-700' },
  failure: { label: 'Failure', color: 'bg-red-100 text-red-700' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
};

const ACTION_ICONS = {
  LOGIN: 'LogIn',
  LOGIN_FAILED: 'ShieldAlert',
  LOGOUT: 'LogOut',
  ROLE_CHANGE: 'UserCog',
  PERMISSION_CHANGE: 'ShieldCheck',
  PASSWORD_RESET: 'KeyRound',
  ACCOUNT_LOCKED: 'Lock',
  UNAUTHORIZED_ACCESS: 'ShieldX',
  DELETE: 'Trash2',
  EXPORT: 'Download',
  UPDATE: 'Edit',
  CREATE: 'Plus',
};

const formatDate = (ts) => {
  if (!ts) return '-';
  return new Date(ts)?.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const formatRelative = (ts) => {
  if (!ts) return '';
  const diff = Date.now() - new Date(ts)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const StatCard = ({ icon, label, value, color, trend }) => (
  <div className="bg-card rounded-xl border border-border p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
        <Icon name={icon} size={18} color="currentColor" />
      </div>
    </div>
    <div className="text-3xl font-bold text-foreground">{value}</div>
    {trend && <p className="text-xs text-muted-foreground mt-1">{trend}</p>}
  </div>
);

const SecurityAuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Security-focused: fetch login events, role changes, permission changes, failures
      let query = supabase
        ?.from('audit_logs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(500);

      if (filterAction !== 'all') query = query?.eq('action', filterAction);
      if (filterSeverity !== 'all') query = query?.eq('severity', filterSeverity);
      if (filterStatus !== 'all') query = query?.eq('status', filterStatus);
      if (dateFrom) query = query?.gte('created_at', dateFrom);
      if (dateTo) query = query?.lte('created_at', dateTo + 'T23:59:59');

      const { data, error: err } = await query;
      if (err) throw err;

      // Filter to security-relevant events
      const securityActions = new Set([
        'LOGIN', 'LOGIN_FAILED', 'LOGOUT', 'ROLE_CHANGE', 'PERMISSION_CHANGE',
        'PASSWORD_RESET', 'ACCOUNT_LOCKED', 'UNAUTHORIZED_ACCESS', 'DELETE', 'EXPORT', 'UPDATE'
      ]);

      const securityLogs = (data || [])?.filter(log =>
        filterAction !== 'all' ||
        securityActions?.has(log?.action) ||
        log?.severity === 'warning' ||
        log?.severity === 'error' ||
        log?.severity === 'critical' ||
        log?.status === 'failure'
      );

      setLogs(securityLogs);
    } catch (err) {
      setError(err?.message || 'Failed to load security logs.');
    } finally {
      setLoading(false);
    }
  }, [filterAction, filterSeverity, filterStatus, dateFrom, dateTo]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { setPage(1); }, [search, filterAction, filterSeverity, filterStatus, dateFrom, dateTo]);

  const filteredLogs = logs?.filter(log => {
    if (!search) return true;
    const s = search?.toLowerCase();
    return (
      log?.action?.toLowerCase()?.includes(s) ||
      log?.resource_type?.toLowerCase()?.includes(s) ||
      log?.resource_name?.toLowerCase()?.includes(s) ||
      log?.user_email?.toLowerCase()?.includes(s) ||
      log?.user_name?.toLowerCase()?.includes(s)
    );
  });

  const totalPages = Math.ceil((filteredLogs?.length || 0) / PAGE_SIZE);
  const paginatedLogs = filteredLogs?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = {
    total: logs?.length || 0,
    loginFailures: logs?.filter(l => l?.action === 'LOGIN_FAILED' || l?.status === 'failure')?.length || 0,
    roleChanges: logs?.filter(l => l?.action === 'ROLE_CHANGE' || l?.action === 'PERMISSION_CHANGE' || l?.resource_type === 'user_role')?.length || 0,
    critical: logs?.filter(l => l?.severity === 'critical' || l?.severity === 'error')?.length || 0,
  };

  const exportCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Resource', 'Severity', 'Status', 'IP Address', 'Details'];
    const rows = filteredLogs?.map(l => [
      formatDate(l?.created_at),
      l?.user_email || '',
      l?.action || '',
      `${l?.resource_type || ''}: ${l?.resource_name || ''}`,
      l?.severity || '',
      l?.status || '',
      l?.ip_address || '',
      JSON.stringify(l?.details || {}),
    ]);
    const csv = [headers, ...rows]?.map(r => r?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(','))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `security-audit-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const getSeverityBadge = (severity) => {
    const cfg = SEVERITY_CONFIG?.[severity] || SEVERITY_CONFIG?.info;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${cfg?.color}`}>{cfg?.label}</span>;
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_CONFIG?.[status] || STATUS_CONFIG?.pending;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.color}`}>{cfg?.label}</span>;
  };

  return (
    <>
      <Helmet><title>Security Audit Log - RIGJOCKEY</title></Helmet>
      <Header />
      <main className="pt-16 min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <Icon name="ShieldAlert" size={16} color="#dc2626" />
                </div>
                <span className="text-xs font-semibold text-red-600 uppercase tracking-wider">Security</span>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Security Audit Log</h1>
              <p className="text-muted-foreground text-sm mt-1">Track login attempts, role changes, permission updates, and security events.</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchLogs}
                className="flex items-center gap-2 px-4 py-2.5 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-smooth"
              >
                <Icon name="RefreshCw" size={15} color="currentColor" />
                Refresh
              </button>
              <button
                onClick={exportCSV}
                className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-lg font-medium text-sm hover:bg-primary/90 transition-smooth"
              >
                <Icon name="Download" size={16} color="white" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard icon="ShieldCheck" label="Security Events" value={stats?.total} color="bg-blue-100 text-blue-600" trend="All tracked events" />
            <StatCard icon="ShieldAlert" label="Login Failures" value={stats?.loginFailures} color="bg-red-100 text-red-600" trend="Failed auth attempts" />
            <StatCard icon="UserCog" label="Role Changes" value={stats?.roleChanges} color="bg-purple-100 text-purple-600" trend="Permission updates" />
            <StatCard icon="AlertTriangle" label="Critical Events" value={stats?.critical} color="bg-orange-100 text-orange-600" trend="High severity" />
          </div>

          {error && (
            <div className="mb-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Filters */}
          <div className="bg-card rounded-xl border border-border p-4 mb-4">
            <div className="flex flex-wrap gap-3">
              <div className="relative flex-1 min-w-48">
                <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search user, action, resource..."
                  value={search}
                  onChange={e => setSearch(e?.target?.value)}
                  className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <select
                value={filterAction}
                onChange={e => setFilterAction(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {SECURITY_EVENT_TYPES?.map(a => (
                  <option key={a} value={a}>{a === 'all' ? 'All Actions' : a?.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <select
                value={filterSeverity}
                onChange={e => setFilterSeverity(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="all">All Severity</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                <option value="all">All Status</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
                <option value="pending">Pending</option>
              </select>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="From"
              />
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e?.target?.value)}
                className="px-3 py-2 border border-border rounded-lg bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="To"
              />
              {(search || filterAction !== 'all' || filterSeverity !== 'all' || filterStatus !== 'all' || dateFrom || dateTo) && (
                <button
                  onClick={() => { setSearch(''); setFilterAction('all'); setFilterSeverity('all'); setFilterStatus('all'); setDateFrom(''); setDateTo(''); }}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-smooth"
                >
                  <Icon name="X" size={14} color="currentColor" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Log Table */}
          <div className="bg-card rounded-xl border border-border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <span className="text-sm font-medium text-foreground">{filteredLogs?.length} events</span>
              <span className="text-xs text-muted-foreground">Page {page} of {totalPages || 1}</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : paginatedLogs?.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="ShieldCheck" size={28} color="var(--color-muted-foreground)" />
                </div>
                <p className="text-foreground font-medium">No security events found</p>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Time</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">User</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Event</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Resource</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Severity</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {paginatedLogs?.map((log) => (
                      <tr
                        key={log?.id}
                        onClick={() => setSelectedLog(selectedLog?.id === log?.id ? null : log)}
                        className="hover:bg-muted/30 cursor-pointer transition-smooth"
                      >
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium text-foreground">{formatRelative(log?.created_at)}</div>
                          <div className="text-xs text-muted-foreground">{formatDate(log?.created_at)}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-foreground">{log?.user_name || '—'}</div>
                          <div className="text-xs text-muted-foreground">{log?.user_email || '—'}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                              <Icon name={ACTION_ICONS?.[log?.action] || 'Activity'} size={14} color="var(--color-foreground)" />
                            </div>
                            <span className="text-sm font-medium text-foreground">{log?.action?.replace(/_/g, ' ')}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm text-foreground">{log?.resource_type || '—'}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-32">{log?.resource_name || '—'}</div>
                        </td>
                        <td className="px-4 py-3">{getSeverityBadge(log?.severity)}</td>
                        <td className="px-4 py-3">{getStatusBadge(log?.status)}</td>
                        <td className="px-4 py-3">
                          <button className="text-xs text-primary hover:underline">
                            {selectedLog?.id === log?.id ? 'Hide' : 'View'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Detail Panel */}
            {selectedLog && (
              <div className="border-t border-border bg-muted/20 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Event Details</h3>
                  <button onClick={() => setSelectedLog(null)}><Icon name="X" size={16} color="var(--color-muted-foreground)" /></button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Action:</span> <span className="font-medium text-foreground ml-1">{selectedLog?.action}</span></div>
                  <div><span className="text-muted-foreground">User:</span> <span className="font-medium text-foreground ml-1">{selectedLog?.user_email || '—'}</span></div>
                  <div><span className="text-muted-foreground">IP:</span> <span className="font-medium text-foreground ml-1">{selectedLog?.ip_address || '—'}</span></div>
                  <div><span className="text-muted-foreground">Time:</span> <span className="font-medium text-foreground ml-1">{formatDate(selectedLog?.created_at)}</span></div>
                </div>
                {selectedLog?.details && Object.keys(selectedLog?.details)?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Additional Data</p>
                    <pre className="text-xs bg-card border border-border rounded-lg p-3 overflow-x-auto text-foreground">
                      {JSON.stringify(selectedLog?.details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-40 transition-smooth"
                >
                  <Icon name="ChevronLeft" size={14} color="currentColor" /> Previous
                </button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted disabled:opacity-40 transition-smooth"
                >
                  Next <Icon name="ChevronRight" size={14} color="currentColor" />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default SecurityAuditLog;
